import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import Lottie from 'lottie-react';
import Select from 'react-select';
import { Slide } from 'react-awesome-reveal';
import { useMutation } from '@apollo/react-hooks';
import { useTranslation } from 'react-i18next';
import { isEmpty, pathOr } from 'ramda';
import { customStyles } from 'utils/styles';

import { UPDATE_PATIENT_PROFILE } from 'apollo/mutations';
import { useGetParams } from 'components/evieOnboarding/utils/useGetParams';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { useToggle } from 'utils/hooks/useToggle';
import { useTabSettings } from 'utils/hooks/useTabSettings';
import { KEYS } from 'components/evieOnboarding/utils/constants';
import { ROUTES } from 'utils/constants';
import { useGetCountries } from 'utils/hooks/useGetCountries';

import signupLogo from 'assets/images/signup-logo.webp';
import heartLoader from 'assets/heart-loader.json';

import Layout from 'components/evieOnboarding/Layout';
import SolidButton from 'components/SolidButton';
import TextInput from 'components/Surveys/FormControlSurveys/FormComponents/TextInput';

import './evie-set-phone.scss';

const EvieSetPhone = () => {
  useTabSettings('Evie', '/evie-favicon.ico');
  const { t } = useTranslation();
  const { urlToken, partner: urlPartner, push } = useGetParams();
  const { dialingCodeList } = useGetCountries();
  const [userToken] = useLocalStorage(KEYS.USER_TOKEN);
  const [userPartner] = useLocalStorage(KEYS.PARTNER);
  const [userId] = useLocalStorage(KEYS.USER_ID);
  const token = urlToken || userToken;
  const partner = urlPartner || userPartner;

  const [country, setCountry] = useState('');
  const [spinner, setSpinner] = useToggle();

  useEffect(() => {
    if (!token && !partner) {
      push(ROUTES.EVIE_ONBOARDING);
    }
  }, []);

  const [updatePatientProfile] = useMutation(UPDATE_PATIENT_PROFILE, {
    context: {
      headers: {
        authorization: token ? `Bearer ${token}` : '',
        role: 'individual',
      },
    },
  });

  const hangleGoDownload = () => {
    push(ROUTES.EVIE_DOWNLOAD);
  };

  const handleSetPhone = async (values) => {
    setSpinner();

    const { phone } = values;
    const { value = '' } = country;
    const countryCoude = value.slice(value.indexOf('+'));
    const phoneResponse = await updatePatientProfile({
      variables: { patientProfile: { phone: `${countryCoude} ${String(phone)}` }, patientId: userId },
    });

    const setPhoneErrors = pathOr([], ['data', 'updatePatientProfile', 'errors'])(phoneResponse);
    const emptyErrors = isEmpty(setPhoneErrors);

    setSpinner();

    if (emptyErrors) {
      hangleGoDownload();
    }
  };

  return (
    <>
      <Layout>
        <section className="setphone-screen">
          <div className="sections__form setphone-screen__form">
            <img className="setphone-screen__form_logo" src={signupLogo} alt="Evie logo" />
            <h2 className="setphone-screen__form_label">{t('evieOnboarding.setPhoneScreen.title')}</h2>
            <p className="setphone-screen__form_description">{t('evieOnboarding.setPhoneScreen.setPhone')}</p>
            <Formik initialValues={{ phone: '' }} validationSchema={null} onSubmit={handleSetPhone}>
              {(formData) => (
                <Form>
                  <div className="form-input setphone-screen__form_input">
                    <Slide duration={300}>
                      <div className="setphone-screen__country">
                        <Select
                          placeholder={t('evieOnboarding.setPhoneScreen.placeholder.country')}
                          options={dialingCodeList}
                          value={country}
                          styles={{
                            ...customStyles,
                            control: (provided) => ({
                              ...provided,
                              border: '.3px solid #8a9bfe',
                              borderRadius: 12,
                              minHeight: 50,
                            }),
                          }}
                          onChange={(value) => setCountry(value)}
                          className="setphone-screen__country_codes"
                        />
                        <TextInput
                          longmessage
                          control="numeric"
                          placeholder={t('evieOnboarding.setPhoneScreen.placeholder.phone')}
                          formikProps={formData}
                          name="phone"
                        />
                      </div>
                    </Slide>
                  </div>
                  <div className="setphone-screen__triggers">
                    <SolidButton
                      disabled={!formData.values.phone || !country}
                      type="submit"
                      className="evie-survey setphone-screen__form_nextbtn"
                    >
                      {t('button.save')}
                    </SolidButton>
                    <button type="button" className="setphone-screen__form_skipbtn" onClick={hangleGoDownload}>
                      {t('evieApp.feedback.skipBtn')}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </section>
      </Layout>
      {spinner && (
        <div className="onboarding-layout__lottie">
          <Lottie animationData={heartLoader} loop style={{ width: 140, height: 110 }} />
        </div>
      )}
    </>
  );
};

export default EvieSetPhone;
